import type { ReactNode } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonthOutlined';
import ExtensionIcon from '@mui/icons-material/ExtensionOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ClockIcon from '@mui/icons-material/AccessTimeOutlined';
import PeopleIcon from '@mui/icons-material/People';
import ApartmentIcon from '@mui/icons-material/Apartment';
import ForumIcon from '@mui/icons-material/Forum';
import { createLoadingShimmerStyles } from '../styles/loader-shimmer';

export type SmallInfoCardProps = {
  iconName?: 'calendar' | 'puzzle' | 'money' | 'location' | 'people' | 'building' | 'chat' | 'clock';
  title: string;
  value: string;
  caption?: string;
  evolutionNumber?: number;
  loading?: boolean;
};

const SmallInfoCard = ({ iconName, title, value, caption, evolutionNumber, loading = false }: SmallInfoCardProps) => {
  const theme = useTheme();

  let icon;
  switch (iconName) {
    case 'calendar':
      icon = <CalendarMonthIcon />;
      break;
    case 'puzzle':
      icon = <ExtensionIcon />;
      break;
    case 'money':
      icon = <MonetizationOnOutlinedIcon />;
      break;
    case 'location':
      icon = <LocationOnOutlinedIcon />;
      break;
    case 'people':
      icon = <PeopleIcon />;
      break;
    case 'building':
      icon = <ApartmentIcon />;
      break;
    case 'chat':
      icon = <ForumIcon />;
      break;
    case 'clock':
      icon = <ClockIcon />;
      break;
  }

  const isEvolutionNumberPositive = evolutionNumber && evolutionNumber > 0;

  const getEvolutionNumberColor = () => {
    if (isEvolutionNumberPositive) {
      return theme.palette.green[80];
    }
    return theme.palette.red[80];
  };

  const getEvolutionValue = () => {
    if (isEvolutionNumberPositive) {
      return `+${evolutionNumber}% ↗`;
    }
    return `${evolutionNumber}% ↘`;
  };

  return (
    <Container loading={loading} hasIcon={Boolean(iconName)}>
      {iconName && <IconContainer>{icon}</IconContainer>}
      <InfoWrapper hasIcon={Boolean(iconName)}>
        <Label>{title}</Label>
        <Value>{value}</Value>
        {caption && (
          <CaptionContainer>
            <Caption>{caption}</Caption>
            {evolutionNumber && (
              <EvolutionNumber color={getEvolutionNumberColor()}>{getEvolutionValue()}</EvolutionNumber>
            )}
          </CaptionContainer>
        )}
      </InfoWrapper>
    </Container>
  );
};

const Container = styled('div', {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'hasIcon',
})<{ loading: boolean; hasIcon: boolean }>(({ theme, loading, hasIcon }) => ({
  borderRadius: '10px',
  backgroundColor: theme.palette.neutral.white,
  padding: '10px',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
  minHeight: hasIcon ? '100px' : 'auto',
  [theme.breakpoints.down('md')]: {
    minHeight: hasIcon ? '76px' : 'auto',
  },
  ...createLoadingShimmerStyles({ active: loading, contentOpacity: 0.6, speedMs: 1200, lightOpacity: 0.6 }),
}));

const IconContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  backgroundColor: theme.palette.neutral[15],
  padding: '10px',
  borderRadius: '10px',
  width: '60px',
  height: '60px',
  [theme.breakpoints.down('md')]: {
    width: '50px',
    height: '50px',
  },
}));

const InfoWrapper = styled('div', { shouldForwardProp: (prop) => prop !== 'hasIcon' })<{ hasIcon: boolean }>(
  ({ hasIcon }) => ({
    display: 'flex',
    flexDirection: 'column',
    padding: hasIcon ? '0 10px' : '0',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    width: '100%',
  }),
);

const Label = styled('h2')(({ theme }) => ({
  fontSize: '18px',
  fontWeight: '500',
  margin: '0',
  lineHeight: '19.6px',
  marginBottom: '4px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '14px',
    lineHeight: '14px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    lineHeight: '12px',
  },
}));

const Value = styled('span')(({ theme }) => ({
  fontFamily: 'Poppins SemiBold',
  fontSize: '14px',
  lineHeight: '19.6px',
  fontWeight: '700',
  textAlign: 'left',
}));

const CaptionContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
}));

const Caption = styled('span')(({ theme }) => ({
  fontFamily: 'Poppins Light',
  fontSize: '14px',
  lineHeight: '19.6px',
}));

const EvolutionNumber = styled('span')<{ color: string }>(({ theme, color }) => ({
  fontSize: '14px',
  lineHeight: '19.6px',
  backgroundColor: theme.palette.neutral[10],
  borderRadius: '20px',
  color,
  padding: '5px 10px',
}));

export { SmallInfoCard };
